import dayjs from 'dayjs';
import type {
  OrderFoodReportModel,
  OrderReportCompositionModel,
  OrderReportDashboardModel,
  OrderReportFoodRankModel,
  OrderReportPaymentCompositionModel,
  OrderReportTrendModel,
} from '@/model/order';

interface TrendAccumulator {
  netRevenue: number;
  orderNumbers: Set<string>;
}

interface FoodAccumulator {
  quantity: number;
  netAmount: number;
}

interface CompositionAccumulator {
  name: string;
  value: number;
}

interface PaymentAccumulator extends CompositionAccumulator {
  orderNumbers: Set<string>;
}

export type OrderReportTrendGranularity = 'day' | 'hour' | 'period';

export type OrderReportBusinessPeriod = '早 00:00-09:30' | '中 09:30-14:00' | '晚 14:00-24:00';

export interface OrderReportPeriodTrendModel {
  date: string;
  period: OrderReportBusinessPeriod;
  amount: number;
  orderCount: number;
  averageOrderAmount: number;
}

const toFiniteNumber = (value: unknown): number => {
  const parsed = typeof value === 'string' ? Number(value.replace(/,/g, '').trim()) : Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const isOrderCancelled = (row: OrderFoodReportModel): boolean =>
  Number(row.orderStatus) === 999 || String(row.orderStatusName || '').includes('取消');

const isFoodCancelled = (row: OrderFoodReportModel): boolean =>
  Number(row.foodStatus) === 999 ||
  String(row.foodStatusName || '').includes('取消') ||
  toFiniteNumber(row.quantity) < 0 ||
  toFiniteNumber(row.amount) < 0 ||
  String(row.settleName || '').includes('退');

const addCompositionValue = (
  target: Map<string, CompositionAccumulator>,
  name: string | undefined,
  value: number,
) => {
  const normalizedName = name?.trim() || '未知';
  const item = target.get(normalizedName) || { name: normalizedName, value: 0 };
  item.value += value;
  target.set(normalizedName, item);
};

export const aggregateOrderReport = (rows: OrderFoodReportModel[]): OrderReportDashboardModel => {
  const effectiveOrders = new Set<string>();
  const trendMap = new Map<string, TrendAccumulator>();
  const foodMap = new Map<string, FoodAccumulator>();
  const paymentMap = new Map<string, PaymentAccumulator>();
  let netRevenue = 0;
  let soldFoodQuantity = 0;
  const orders = new Map<
    string,
    { orderCancelled: boolean; foodCancelled: boolean; paymentStatus: number }
  >();

  rows.forEach((row, index) => {
    const amount = toFiniteNumber(row.amount);
    const quantity = toFiniteNumber(row.quantity);
    const orderKey = row.orderNo?.trim() || `row-${index}`;
    const date = dayjs(row.createTime).isValid()
      ? dayjs(row.createTime).format('YYYY-MM-DD')
      : '未知日期';
    const trend = trendMap.get(date) || { netRevenue: 0, orderNumbers: new Set<string>() };
    const foodName = [row.shopFoodName?.trim() || '未知菜品', row.foodSpecName?.trim()]
      .filter(Boolean)
      .join(' / ');
    const food = foodMap.get(foodName) || { quantity: 0, netAmount: 0 };
    const order = orders.get(orderKey) || {
      orderCancelled: false,
      foodCancelled: false,
      paymentStatus: Number(row.paymentStatus),
    };

    order.orderCancelled ||= isOrderCancelled(row);
    order.foodCancelled ||= isFoodCancelled(row);
    if (Number(row.paymentStatus) > order.paymentStatus) {
      order.paymentStatus = Number(row.paymentStatus);
    }
    orders.set(orderKey, order);

    netRevenue += amount;
    soldFoodQuantity += quantity;
    trend.netRevenue += amount;
    food.quantity += quantity;
    food.netAmount += amount;
    const paymentName = row.paymentMethodName?.trim() || '未知';
    const payment = paymentMap.get(paymentName) || {
      name: paymentName,
      value: 0,
      orderNumbers: new Set<string>(),
    };
    payment.value += amount;
    payment.orderNumbers.add(orderKey);
    paymentMap.set(paymentName, payment);
    if (!isOrderCancelled(row)) {
      effectiveOrders.add(orderKey);
      trend.orderNumbers.add(orderKey);
    }
    trendMap.set(date, trend);
    foodMap.set(foodName, food);
  });

  const trend: OrderReportTrendModel[] = Array.from(trendMap.entries())
    .map(([date, item]) => ({
      date,
      netRevenue: Math.round(item.netRevenue * 100) / 100,
      orderCount: item.orderNumbers.size,
    }))
    .sort((left, right) => left.date.localeCompare(right.date));

  const foodRanking: OrderReportFoodRankModel[] = Array.from(foodMap.entries()).map(
    ([name, item]) => ({
      name,
      quantity: Math.round(item.quantity * 100) / 100,
      netAmount: Math.round(item.netAmount * 100) / 100,
    }),
  );

  const paymentComposition: OrderReportPaymentCompositionModel[] = Array.from(paymentMap.values())
    .map((item) => {
      const value = Math.round(item.value * 100) / 100;
      return {
        name: item.name,
        value,
        orderCount: item.orderNumbers.size,
        averageOrderAmount:
          item.orderNumbers.size > 0 ? Math.round((value / item.orderNumbers.size) * 100) / 100 : 0,
      };
    })
    .filter((item) => item.value > 0)
    .sort((left, right) => right.value - left.value);

  const cancellationCounts = {
    normal: 0,
    unpaid: 0,
    paid: 0,
    partial: 0,
  };
  orders.forEach((order) => {
    if (order.orderCancelled) {
      if (order.paymentStatus > 0) cancellationCounts.paid += 1;
      else cancellationCounts.unpaid += 1;
    } else if (order.foodCancelled) {
      cancellationCounts.partial += 1;
    } else {
      cancellationCounts.normal += 1;
    }
  });
  const cancelledOrderCount =
    cancellationCounts.unpaid + cancellationCounts.paid + cancellationCounts.partial;

  return {
    kpi: {
      effectiveOrderCount: effectiveOrders.size,
      netRevenue: Math.round(netRevenue * 100) / 100,
      soldFoodQuantity: Math.round(soldFoodQuantity * 100) / 100,
      averageOrderAmount:
        effectiveOrders.size > 0 ? Math.round((netRevenue / effectiveOrders.size) * 100) / 100 : 0,
    },
    trend,
    foodRanking,
    paymentComposition,
    cancellation: {
      totalOrderCount: orders.size,
      cancelledOrderCount,
      composition: [
        { name: '正常订单', value: cancellationCounts.normal },
        { name: '未支付取消', value: cancellationCounts.unpaid },
        { name: '已支付取消', value: cancellationCounts.paid },
        { name: '部分取消', value: cancellationCounts.partial },
      ].filter((item) => item.value > 0),
    },
  };
};

export const aggregateOrderReportTrend = (
  rows: OrderFoodReportModel[],
  granularity: OrderReportTrendGranularity,
): OrderReportTrendModel[] => {
  const trendMap = new Map<string, TrendAccumulator>();
  rows.forEach((row, index) => {
    const createTime = dayjs(row.createTime);
    const period = createTime.isValid()
      ? createTime.format(granularity === 'hour' ? 'YYYY-MM-DD HH:00' : 'YYYY-MM-DD')
      : '未知时间';
    const orderKey = row.orderNo?.trim() || `row-${index}`;
    const item = trendMap.get(period) || { netRevenue: 0, orderNumbers: new Set<string>() };
    item.netRevenue += toFiniteNumber(row.amount);
    if (!isOrderCancelled(row)) {
      item.orderNumbers.add(orderKey);
    }
    trendMap.set(period, item);
  });
  return Array.from(trendMap.entries())
    .map(([date, item]) => ({
      date,
      netRevenue: Math.round(item.netRevenue * 100) / 100,
      orderCount: item.orderNumbers.size,
    }))
    .sort((left, right) => left.date.localeCompare(right.date));
};

export const aggregateOrderReportPeriodTrend = (
  rows: OrderFoodReportModel[],
): OrderReportPeriodTrendModel[] => {
  const periodMap = new Map<string, TrendAccumulator>();
  rows.forEach((row, index) => {
    const createTime = dayjs(row.createTime);
    if (!createTime.isValid()) return;
    const minutes = createTime.hour() * 60 + createTime.minute();
    const period: OrderReportBusinessPeriod =
      minutes < 9 * 60 + 30
        ? '早 00:00-09:30'
        : minutes < 14 * 60
        ? '中 09:30-14:00'
        : '晚 14:00-24:00';
    const date = createTime.format('YYYY-MM-DD');
    const key = `${date}|${period}`;
    const orderKey = row.orderNo?.trim() || `row-${index}`;
    const item = periodMap.get(key) || { netRevenue: 0, orderNumbers: new Set<string>() };
    item.netRevenue += toFiniteNumber(row.amount);
    if (!isOrderCancelled(row)) {
      item.orderNumbers.add(orderKey);
    }
    periodMap.set(key, item);
  });

  const periodOrder: OrderReportBusinessPeriod[] = [
    '早 00:00-09:30',
    '中 09:30-14:00',
    '晚 14:00-24:00',
  ];
  return Array.from(periodMap.entries())
    .map(([key, item]) => {
      const separatorIndex = key.indexOf('|');
      const date = key.slice(0, separatorIndex);
      const period = key.slice(separatorIndex + 1) as OrderReportBusinessPeriod;
      const amount = Math.round(item.netRevenue * 100) / 100;
      return {
        date,
        period,
        amount,
        orderCount: item.orderNumbers.size,
        averageOrderAmount:
          item.orderNumbers.size > 0
            ? Math.round((amount / item.orderNumbers.size) * 100) / 100
            : 0,
      };
    })
    .sort(
      (left, right) =>
        left.date.localeCompare(right.date) ||
        periodOrder.indexOf(left.period) - periodOrder.indexOf(right.period),
    );
};

export const aggregateFoodCategoryComposition = (
  rows: OrderFoodReportModel[],
  categoryByFoodName: ReadonlyMap<string, string>,
): OrderReportCompositionModel[] => {
  const categoryMap = new Map<string, CompositionAccumulator>();
  rows.forEach((row) => {
    const foodName = row.shopFoodName?.trim() || '';
    const categoryName = categoryByFoodName.get(foodName) || '未分类';
    addCompositionValue(categoryMap, categoryName, toFiniteNumber(row.quantity));
  });
  return Array.from(categoryMap.values())
    .map((item) => ({ ...item, value: Math.round(item.value * 100) / 100 }))
    .filter((item) => item.value > 0)
    .sort((left, right) => right.value - left.value);
};
